<!-- Modal Header -->
<div class="modal-header border-0 pb-0">
    <div class="w-100">
        <!-- Company Name + Close -->
        <div class="d-flex align-items-center justify-content-between mb-3">
            <h5 class="modal-title mb-0">
                <i class="ti ti-building me-2"></i>
                {{ task()?.company?.name }}
            </h5>
            <button type="button" class="btn-close" (click)="close()"></button>
        </div>

        <!-- Status Badge + Estimated Value -->
        <div class="d-flex align-items-center gap-2 mb-3">
            <span class="badge" [class]="'bg-' + getStatusColor(task()?.status)">
                {{ getStatusLabel(task()?.status) }}
            </span>
            <span class="text-muted">•</span>
            <span class="fw-semibold text-primary">{{ task()?.estimatedDebtValue | currency }}
                <i class="ti ti-info-circle cursor-pointer"
                   ngbTooltip="Esse valor é uma fotografia do valor estimado da dívida no momento da criação da tarefa. Ele não é atualizado automaticamente, então pode não refletir o valor atual da dívida." placement="top"></i>
            </span>
        </div>
    </div>
</div>

<!-- Modal Body -->
<div class="modal-body">
    @if (isLoading()) {
        <div class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
        </div>
    } @else {
        <div class="row">
            <!-- Left Column: Main Content -->
            <div class="col-lg-8">
                <!-- Tabs: Comments & Timeline -->
                <ul class="nav nav-tabs mb-3">
                    <li class="nav-item">
                        <button
                                class="nav-link"
                                [class.active]="activeTab() === 'comments'"
                                (click)="activeTab.set('comments')">
                            <i class="ti ti-message-circle me-1"></i>
                            Comentários ({{ comments().length }})
                        </button>
                    </li>
                    <li class="nav-item">
                        <button
                                class="nav-link"
                                [class.active]="activeTab() === 'timeline'"
                                (click)="activeTab.set('timeline')">
                            <i class="ti ti-timeline me-1"></i>
                            Histórico ({{ timeline().length }})
                        </button>
                    </li>
                </ul>

                <!-- Comments Tab -->
                @if (activeTab() === 'comments') {
                    <div class="comments-section">
                        <!-- Add Comment -->
                        <div class="add-comment mb-4">
                            <label class="form-label fw-semibold">Adicionar Comentário</label>
                            <app-rich-text
                                    [minHeight]="100"
                                    [(ngModel)]="newComment"
                                    [placeholder]="'Escreva um comentário...'"
                            />
                            <div class="d-flex justify-content-end mt-2">
                                <button
                                        class="btn btn-primary btn-sm"
                                        [disabled]="!newComment.trim() || isSavingComment()"
                                        (click)="addComment()">
                                    @if (isSavingComment()) {
                                        <span class="spinner-border spinner-border-sm me-1"></span>
                                    }
                                    <i class="ti ti-send"></i> Comentar
                                </button>
                            </div>
                        </div>

                        <!-- Comments List -->
                        <div class="comments-list">
                            @for (comment of comments(); track comment.id) {
                                <div class="comment-item">
                                    <div class="comment-avatar">
                                        <div class="avatar-circle">
                                            {{ getUserInitials(comment.author) }}
                                        </div>
                                    </div>
                                    <div class="comment-content">
                                        <div class="comment-header">
                                            <span class="comment-author">{{ comment.author?.firstName }} {{ comment.author?.lastName }}</span>
                                            <span class="comment-date">{{ comment.createdAt | date:'dd/MM/yyyy HH:mm' }}</span>
                                        </div>
                                        <div class="comment-body" [innerHTML]="comment.content"></div>
                                    </div>
                                </div>
                            } @empty {
                                <div class="text-center text-muted py-4">
                                    <i class="ti ti-message-off fs-1"></i>
                                    <p class="mb-0 mt-2">Nenhum comentário ainda</p>
                                </div>
                            }
                        </div>
                    </div>
                }

                <!-- Timeline Tab -->
                @if (activeTab() === 'timeline') {
                    <div class="timeline-section">
                        @for (event of timeline(); track event.id) {
                            <div class="timeline-item">
                                <div class="timeline-marker" [class]="'marker-' + event.eventType"></div>
                                <div class="timeline-content">
                                    <div class="timeline-header">
                                        <span class="timeline-author">
                                            @if (event.author) {
                                                {{ event.author.firstName }} {{ event.author.lastName }}
                                            } @else {
                                                Sistema
                                            }
                                        </span>
                                        <span class="timeline-date">{{ event.createdAt | date:'dd/MM/yyyy HH:mm' }}</span>
                                    </div>
                                    <div class="timeline-description">{{ event.description }}</div>

                                    <!-- Metadata Details -->
                                    @if (event.metadata && hasMetadata(event.metadata)) {
                                        <div class="timeline-metadata">
                                            @if (event.eventType === 'STATUS_CHANGED' && event.metadata.oldStatus && event.metadata.newStatus) {
                                                <div class="metadata-item">
                                                    <span class="badge bg-light text-dark">{{ getStatusLabel(event.metadata.oldStatus) }}</span>
                                                    <i class="ti ti-arrow-right mx-1 text-muted"></i>
                                                    <span class="badge" [class]="'bg-' + getStatusColor(event.metadata.newStatus)">{{ getStatusLabel(event.metadata.newStatus) }}</span>
                                                </div>
                                            }

                                            @if (event.eventType === 'ASSIGNEE_CHANGED') {
                                                <div class="metadata-item">
                                                    @if (event.metadata.oldAssignee) {
                                                        <span class="text-muted small">De: Vendedor #{{ event.metadata.oldAssignee }}</span>
                                                    } @else {
                                                        <span class="text-muted small">De: Não atribuído</span>
                                                    }
                                                    <i class="ti ti-arrow-right mx-1 text-muted"></i>
                                                    @if (event.metadata.newAssignee) {
                                                        <span class="text-primary small">Para: Vendedor #{{ event.metadata.newAssignee }}</span>
                                                    } @else {
                                                        <span class="text-muted small">Para: Não atribuído</span>
                                                    }
                                                </div>
                                            }

                                            @if (event.eventType === 'COMMENT_ADDED' && event.metadata.commentId) {
                                                <div class="metadata-item">
                                                    <i class="ti ti-message-circle me-1"></i>
                                                    <span class="text-muted small">Comentário #{{ event.metadata.commentId }}</span>
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            </div>
                        } @empty {
                            <div class="text-center text-muted py-4">
                                <i class="ti ti-timeline-event-x fs-1"></i>
                                <p class="mb-0 mt-2">Nenhum evento registrado</p>
                            </div>
                        }
                    </div>
                }
            </div>

            <!-- Right Column: Sidebar with Details -->
            <div class="col-lg-4">
                <div class="task-sidebar">
                    <!-- Assignee -->
                    <div class="sidebar-section">
                        <label class="sidebar-label">Vendedor</label>
                        @if (allowAssigneeManagement) {
                            <!-- Editable assignee selector -->
                            <div class="assignee-selector">
                                <button
                                        class="assignee-button"
                                        (click)="openAssigneeDropdown.set(!openAssigneeDropdown())">
                                    @if (task()?.assignee) {
                                        <div class="avatar-circle me-2">
                                            {{ getUserInitials(task().assignee) }}
                                        </div>
                                        <span>{{ task().assignee.firstName }} {{ task().assignee.lastName }}</span>
                                    } @else {
                                        <i class="ti ti-user-plus me-2"></i>
                                        <span class="text-muted">Atribuir vendedor</span>
                                    }
                                </button>

                                <!-- Dropdown -->
                                @if (openAssigneeDropdown()) {
                                    <div class="assignee-dropdown">
                                        @if (task()?.assignee) {
                                            <button class="assignee-option" (click)="unassignUser()">
                                                <i class="ti ti-user-minus me-2"></i>
                                                <span>Remover vendedor</span>
                                            </button>
                                            <div class="dropdown-divider"></div>
                                        }

                                        @for (user of users(); track user.id) {
                                            <button class="assignee-option" (click)="assignUser(user)">
                                                <div class="avatar-circle me-2">
                                                    {{ getUserInitials(user) }}
                                                </div>
                                                <span>{{ user.firstName }} {{ user.lastName }}</span>
                                            </button>
                                        }
                                    </div>
                                }
                            </div>
                        } @else {
                            <!-- Read-only assignee display -->
                            @if (task()?.assignee) {
                                <div class="d-flex align-items-center p-2 bg-light rounded">
                                    <div class="avatar-circle me-2">
                                        {{ getUserInitials(task().assignee) }}
                                    </div>
                                    <span>{{ task().assignee.firstName }} {{ task().assignee.lastName }}</span>
                                </div>
                            } @else {
                                <div class="text-muted p-2">
                                    <i class="ti ti-user-off me-1"></i>
                                    Sem vendedor atribuído
                                </div>
                            }
                        }
                    </div>

                    <!-- Company Info -->
                    <div class="sidebar-section">
                        <label class="sidebar-label">Empresa</label>
                        <div class="company-info">
                            <div class="info-item">
                                <i class="ti ti-building text-muted"></i>
                                <span class="text-truncate">{{ task()?.company?.name }}</span>
                            </div>
                            <div class="info-item">
                                <i class="ti ti-id text-muted"></i>
                                <span>{{ task()?.company?.documentNumber | cpfCnpj }}</span>
                            </div>
                            <div class="info-item">
                                <i class="ti ti-map-pin text-muted"></i>
                                <span>{{ task()?.company?.state }}</span>
                            </div>
                            @if (task()?.company?.phones && task()?.company?.phones.length > 0) {
                                @for (phone of task()?.company?.phones; track $index) {
                                    <div class="info-item">
                                        <i class="ti ti-phone text-muted"></i>
                                        <app-phone [areaCode]="phone.ddd" [phone]="phone.number" [isFax]="phone.isFax"/>
                                    </div>
                                }
                            }
                            <button class="btn btn-sm btn-outline-primary w-100 mt-2" (click)="viewCompanyDetails()">
                                <i class="ti ti-eye me-1"></i>
                                Ver Detalhes da Empresa
                            </button>
                            <button class="btn btn-sm btn-outline-dark w-100 mt-2" (click)="viewDebts()">
                                <i class="ti ti-file-invoice me-2"></i>
                                Ver Parcelamentos PGFN
                            </button>
                            <button class="btn btn-sm btn-outline-success w-100 mt-2" (click)="openNegotiationStrategy()">
                                <i class="ti ti-brain me-1"></i>
                                Estratégia de Negociação IA
                            </button>
                            @if (allowRemoveFromCampaign) {
                                <button class="btn btn-sm btn-outline-danger w-100 mt-2" (click)="removeCompanyFromCampaign()">
                                    <i class="ti ti-trash me-1"></i>
                                    Remover da Campanha
                                </button>
                            }
                        </div>
                    </div>

                    <!-- Datas -->
                    <div class="sidebar-section">
                        <label class="sidebar-label">Datas</label>
                        <div class="dates-info">
                            <div class="info-item">
                                <span class="info-label">Criado em:</span>
                                <span class="info-value">{{ task()?.createdAt | date:'dd/MM/yyyy HH:mm' }}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Atualizado em:</span>
                                <span class="info-value">{{ task()?.updatedAt | date:'dd/MM/yyyy HH:mm' }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    }
</div>
